import React from "react";

/**
 * One field of a staged card, and the mobile keyboard rules that go with it.
 *
 * DY2.3. The staged flow shows one thing at a time, so every stage change moves
 * focus — and on a phone, moving focus opens the keyboard, which is where the
 * old single-card form fell apart: the browser scrolled the focused input into
 * view with its own animation while the transcript above was still growing, and
 * the conversation jumped under the reader's thumb.
 *
 * Three rules, all of them here so no caller has to remember them:
 *
 *   * `block: "nearest"` and `behavior: "auto"`. Never `"smooth"` with a
 *     keyboard opening: the two animations run at once and the card ends up
 *     somewhere neither of them intended. "nearest" scrolls the minimum, so a
 *     field already on screen does not move at all.
 *   * The scroll happens on the wrapper, not the input. Scrolling the input
 *     alone puts its label off the top of the screen, and a text box with no
 *     label above it is the thing people cannot answer.
 *   * `autoFocus` is opt-in per stage rather than automatic. The first stage of
 *     a card must NOT steal focus — the card appears while the person is still
 *     reading Dany's message, and a keyboard that opens by itself hides it.
 */
export default function DanyStageField({
  label, hint, autoFocus = false, children,
}) {
  const box = React.useRef(null);

  React.useEffect(() => {
    if (!autoFocus) return;
    // One frame later: the stage has rendered, so the wrapper has a position.
    const id = requestAnimationFrame(() => {
      box.current?.scrollIntoView({ behavior: "auto", block: "nearest" });
    });
    return () => cancelAnimationFrame(id);
  }, [autoFocus]);

  return (
    <label ref={box} style={{ display: "block", marginBottom: "var(--space-3)" }}>
      <span style={{
        display: "block", marginBottom: 4, fontFamily: "var(--font-display)",
        fontSize: "var(--text-xs)", textTransform: "uppercase",
        letterSpacing: "var(--tracking-widest)", color: "var(--dany-meta)",
      }}>{label}</span>
      {children}
      {hint && (
        <span style={{
          display: "block", marginTop: 4, fontSize: "var(--text-xs)",
          color: "var(--text-subtle)", lineHeight: "var(--leading-relaxed)",
        }}>{hint}</span>
      )}
    </label>
  );
}
